import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './TrackingSettings.css';
import { useTranslation } from 'react-i18next';
import { 
    FaPalette, 
    FaEdit, 
    FaTrash, 
    FaSpinner,
    FaSearch,
    FaExclamationTriangle,
    FaCheck,
    FaTimes,
    FaEye,
    FaEyeSlash,
    FaToggleOn,
    FaToggleOff,
    FaSave,
    FaUndo,
    FaImage,
    FaBuilding,
    FaCode,
    FaEnvelope,
    FaKey,
    FaBox,  // Changed from FaPackage to FaBox
    FaArrowLeft
} from 'react-icons/fa';

const TrackingSettings = () => {
    const [user, setUser] = useState(null);
    const [settings, setSettings] = useState(null);
    const [brands, setBrands] = useState([]);
    const [selectedBrandId, setSelectedBrandId] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const navigate = useNavigate();
    const { t } = useTranslation();

    // Form state
    const [formData, setFormData] = useState({
        colors: {
            primary_color: '#2d2d2d',
            secondary_color: '#f8f9fa',
            background_color: '#ffffff',
            text_color: '#333333',
            border_color: '#e0e0e0',
            success_color: '#28a745',
            warning_color: '#ffc107',
            error_color: '#dc3545'
        },
        banner: {
            enabled: false,
            title: 'Track Your Package',
            subtitle: '',
            background_image: '',
            background_color: '#f8f9fa',
            text_color: '#333333',
            height: 200
        },
        branding: {
            company_name: '',
            company_logo: '',
            show_powered_by: true
        },
        custom_css: ''
    });

    // Preview modal states
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewStep, setPreviewStep] = useState('email-verification');

    // Delete confirmation modal states
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('access_token');
        if (!token) {
            navigate('/');
            return;
        }

        const fetchData = async () => {
            try {
                setIsLoading(true);
                const userData = await fetchUserData(token);
                await fetchSettings(token);
                
                // Fetch brands if user has brand management
                if (userData?.has_brand) {
                    await fetchBrands(token);
                }
            } catch (error) {
                console.error('Error loading tracking settings data:', error);
                setError('Failed to load tracking settings');
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [navigate, selectedBrandId]);

    const fetchUserData = async (token) => {
        try {
            const response = await axios.get('/api/users/user', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUser(response.data);
            return response.data;
        } catch (error) {
            console.error('Failed to fetch user data:', error);
            setUser(null);
            navigate('/');
            return null;
        }
    };

    const fetchBrands = async (token) => {
        try {
            const response = await axios.get('/api/brands/list', {
                headers: { Authorization: `Bearer ${token}` }
            });
            setBrands(response.data || []);
        } catch (error) {
            console.error('Failed to fetch brands:', error);
            setBrands([]);
        }
    };

    const fetchSettings = async (token) => {
        try {
            const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
            const response = await axios.get('/api/tracking_settings/settings', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });
            
            setSettings(response.data);
            setFormData({
                colors: response.data.colors,
                banner: response.data.banner,
                branding: response.data.branding,
                custom_css: response.data.custom_css || ''
            });
        } catch (error) {
            console.error('Failed to fetch tracking settings:', error);
            setSettings(null);
        }
    };

    const canManageSettings = () => {
        return user && ['admin', 'seller', 'eshop'].includes(user.role);
    };

    const getBrandName = (brandId) => {
        if (!brandId) return t('trackingSettings.defaultSettings', 'Default Settings');
        const brand = brands.find(b => b.id === brandId);
        return brand ? brand.name : t('trackingSettings.unknownBrand', 'Unknown Brand');
    };

    const handleBrandChange = (brandId) => {
        setSelectedBrandId(brandId);
    };

    const handleInputChange = (section, field, value) => {
        setFormData(prev => ({
            ...prev,
            [section]: {
                ...prev[section],
                [field]: value
            }
        }));
    };

    const handleCustomCssChange = (value) => {
        setFormData(prev => ({
            ...prev,
            custom_css: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canManageSettings()) return;

        setIsSubmitting(true);
        setError(null);
        setSuccess(null);

        const token = localStorage.getItem('access_token');

        try {
            const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
            await axios.put('/api/tracking_settings/settings', formData, {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setSuccess(t('settingsUpdatedSuccessfully', 'Tracking settings updated successfully!'));
            await fetchSettings(token);
            
            setTimeout(() => {
                setSuccess(null);
            }, 3000);
        } catch (error) {
            console.error('Failed to update tracking settings:', error);
            setError(
                error.response?.data?.detail || 
                t('failedToUpdateSettings', 'Failed to update tracking settings. Please try again.')
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = () => {
        if (settings) {
            setFormData({
                colors: settings.colors,
                banner: settings.banner,
                branding: settings.branding,
                custom_css: settings.custom_css || ''
            });
        }
    };

    const handleDeleteSettings = () => {
        if (!canManageSettings()) return;
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        setIsDeleting(true);
        const token = localStorage.getItem('access_token');

        try {
            const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
            await axios.delete('/api/tracking_settings/settings', {
                headers: { Authorization: `Bearer ${token}` },
                params
            });

            setSuccess(t('settingsDeletedSuccessfully', 'Tracking settings deleted successfully!'));
            await fetchSettings(token);
            setIsDeleteModalOpen(false);
            
            setTimeout(() => {
                setSuccess(null);
            }, 3000);
        } catch (error) {
            console.error('Failed to delete tracking settings:', error);
            setError(
                error.response?.data?.detail || 
                t('failedToDeleteSettings', 'Failed to delete tracking settings. Please try again.')
            );
        } finally {
            setIsDeleting(false);
        }
    };

    const handlePreview = () => {
        setPreviewStep('email-verification');
        setIsPreviewModalOpen(true);
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const renderPreviewContent = () => {
        const styles = {
            backgroundColor: formData.colors.background_color,
            color: formData.colors.text_color,
            '--primary-color': formData.colors.primary_color,
            '--secondary-color': formData.colors.secondary_color,
            '--success-color': formData.colors.success_color,
            '--warning-color': formData.colors.warning_color,
            '--error-color': formData.colors.error_color,
            '--border-color': formData.colors.border_color,
        };

        const bannerStyles = formData.banner.enabled ? {
            backgroundColor: formData.banner.background_color,
            color: formData.banner.text_color,
            height: `${formData.banner.height}px`,
            backgroundImage: formData.banner.background_image ? `url(${formData.banner.background_image})` : 'none',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        } : {};

        return (
            <div className="una-tracking-preview-container" style={styles}>
                {/* Banner Section */}
                {formData.banner.enabled && (
                    <div className="una-tracking-banner" style={bannerStyles}>
                        <div className="una-tracking-banner-overlay">
                            <div className="una-tracking-banner-content">
                                <h1>{formData.banner.title}</h1>
                                {formData.banner.subtitle && <p>{formData.banner.subtitle}</p>}
                            </div>
                        </div>
                    </div>
                )}

                {/* Header (if banner is not enabled) */}
                {!formData.banner.enabled && (
                    <div className="una-tracking-header">
                        <h1>{formData.banner.title}</h1>
                    </div>
                )}

                {/* Email Verification Step */}
                {previewStep === 'email-verification' && (
                    <div className="una-verification-step">
                        <div className="una-step-icon">
                            <FaEnvelope />
                        </div>
                        <h3>Access Your Orders</h3>
                        <p>Enter your email address to view your order tracking information</p>
                        <div className="una-preview-form">
                            <div className="una-form-field">
                                <label>Customer Email</label>
                                <input 
                                    type="email" 
                                    placeholder="customer@example.com" 
                                    style={{ borderColor: formData.colors.border_color }}
                                />
                            </div>
                            <button 
                                className="una-submit-button"
                                style={{ backgroundColor: formData.colors.primary_color }}
                                onClick={() => setPreviewStep('code-verification')}
                            >
                                Send Verification Code
                            </button>
                        </div>
                    </div>
                )}

                {/* Code Verification Step */}
                {previewStep === 'code-verification' && (
                    <div className="una-verification-step">
                        <div className="una-step-icon">
                            <FaKey />
                        </div>
                        <h3>Enter Verification Code</h3>
                        <p>Please enter the 6-digit code sent to your email</p>
                        <div className="una-preview-form">
                            <div className="una-form-field">
                                <label>Verification Code</label>
                                <input 
                                    type="text" 
                                    placeholder="000000" 
                                    maxLength="6"
                                    className="una-code-input"
                                    style={{ borderColor: formData.colors.border_color }}
                                />
                            </div>
                            <div className="una-button-group">
                                <button 
                                    className="una-secondary-button"
                                    style={{ borderColor: formData.colors.border_color }}
                                    onClick={() => setPreviewStep('email-verification')}
                                >
                                    <FaArrowLeft /> Back to Email
                                </button>
                                <button 
                                    className="una-submit-button"
                                    style={{ backgroundColor: formData.colors.primary_color }}
                                    onClick={() => setPreviewStep('orders-list')}
                                >
                                    Verify Code
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Orders List Step */}
                {previewStep === 'orders-list' && (
                    <div className="una-verification-step">
                        <div className="una-step-icon">
                            <FaBox />  {/* Changed from FaPackage to FaBox */}
                        </div>
                        <h2>Select Order to Track</h2>
                        <div className="una-orders-list">
                            {[1, 2, 3].map(orderId => (
                                <div 
                                    key={orderId} 
                                    className="una-order-item"
                                    style={{ borderColor: formData.colors.border_color }}
                                    onClick={() => setPreviewStep('tracking-results')}
                                >
                                    <div className="una-order-info">
                                        <h4>Order #ORD-2024-{orderId.toString().padStart(3, '0')}</h4>
                                        <p>Placed on March {orderId + 10}, 2024</p>
                                        <div className="una-order-status">
                                            <span 
                                                className="una-status-badge"
                                                style={{ backgroundColor: orderId === 1 ? formData.colors.success_color : formData.colors.warning_color }}
                                            >
                                                {orderId === 1 ? 'Delivered' : 'In Transit'}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="una-order-amount">
                                        ${(99.99 + orderId * 20).toFixed(2)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Tracking Results Step */}
                {previewStep === 'tracking-results' && (
                    <div className="una-tracking-results">
                        <div className="una-tracking-results-header">
                            <div className="una-tracking-title-section">
                                <h1>Order #ORD-2024-001</h1>
                                <p>Tracking Number: TR123456789</p>
                            </div>
                            <div className="una-tracking-actions">
                                {formData.branding.company_logo && (
                                    <img 
                                        src={formData.branding.company_logo} 
                                        alt="Company Logo" 
                                        className="una-company-logo"
                                    />
                                )}
                                <button 
                                    className="una-back-button"
                                    style={{ borderColor: formData.colors.border_color }}
                                    onClick={() => setPreviewStep('orders-list')}
                                >
                                    <FaArrowLeft /> Back to Orders
                                </button>
                            </div>
                        </div>

                        <div className="una-tracking-timeline">
                            <div className="una-timeline-item completed">
                                <div 
                                    className="una-timeline-marker"
                                    style={{ backgroundColor: formData.colors.success_color }}
                                ></div>
                                <div className="una-timeline-content">
                                    <h4>Order Confirmed</h4>
                                    <p>March 15, 2024 at 10:30 AM</p>
                                    <small>Your order has been confirmed and is being prepared.</small>
                                </div>
                            </div>

                            <div className="una-timeline-item completed">
                                <div 
                                    className="una-timeline-marker"
                                    style={{ backgroundColor: formData.colors.success_color }}
                                ></div>
                                <div className="una-timeline-content">
                                    <h4>Package Shipped</h4>
                                    <p>March 16, 2024 at 2:15 PM</p>
                                    <small>Your package has been shipped and is on its way.</small>
                                </div>
                            </div>

                            <div className="una-timeline-item active">
                                <div 
                                    className="una-timeline-marker"
                                    style={{ backgroundColor: formData.colors.warning_color }}
                                ></div>
                                <div className="una-timeline-content">
                                    <h4>Out for Delivery</h4>
                                    <p>March 18, 2024 at 8:00 AM</p>
                                    <small>Your package is out for delivery and will arrive today.</small>
                                </div>
                            </div>

                            <div className="una-timeline-item pending">
                                <div 
                                    className="una-timeline-marker"
                                    style={{ backgroundColor: formData.colors.border_color }}
                                ></div>
                                <div className="una-timeline-content">
                                    <h4>Delivered</h4>
                                    <p>Expected today</p>
                                    <small>Your package will be delivered to your address.</small>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Powered By Footer */}
                {formData.branding.show_powered_by && (
                    <div className="una-powered-by">
                        Powered by <strong>{formData.branding.company_name || 'Unanimity AI'}</strong>
                    </div>
                )}

                {/* Custom CSS Preview */}
                {formData.custom_css && (
                    <style dangerouslySetInnerHTML={{ __html: formData.custom_css }} />
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="una-tracking-settings-loading">
                <FaSpinner className="una-spinner" />
                <h2>{t('loadingTrackingSettings', 'Loading tracking settings...')}</h2>
            </div>
        );
    }

    return (
        <div className="una-tracking-settings-container">
            <div className="una-tracking-settings-header">
                <div className="una-tracking-settings-title">
                    <FaPalette className="una-tracking-settings-icon" />
                    <h1>{t('trackingSettings', 'Tracking Settings')}</h1>
                </div>
                
                <div className="una-tracking-settings-actions">
                    <button 
                        className="una-preview-button"
                        onClick={handlePreview}
                        type="button"
                    >
                        <FaEye />
                        {t('preview', 'Preview')}
                    </button>
                    
                    {canManageSettings() && settings && (
                        <button 
                            className="una-delete-settings-button"
                            onClick={handleDeleteSettings}
                            type="button"
                        >
                            <FaTrash />
                            {t('deleteSettings', 'Delete Settings')}
                        </button>
                    )}
                </div>
            </div>

            <div className="una-tracking-settings-content">
                {error && <div className="una-error-message">{error}</div>}
                {success && <div className="una-success-message">{success}</div>}

                {/* Brand Selection Section - Only show for users with brands */}
                {user?.has_brand && brands.length > 0 && (
                    <div className="una-brand-selection-section">
                        <h3>
                            <FaBuilding />
                            {t('brandSelection', 'Brand Selection')}
                        </h3>
                        <div className="una-brand-selector">
                            <label htmlFor="brand-select">
                                {t('selectBrand', 'Select Brand')}
                            </label>
                            <select
                                id="brand-select"
                                value={selectedBrandId}
                                onChange={(e) => handleBrandChange(e.target.value)}
                                className="una-brand-select"
                            >
                                <option value="">
                                    {t('trackingSettings.defaultSettings', 'Default Settings')}
                                </option>
                                {brands.map(brand => (
                                    <option key={brand.id} value={brand.id}>
                                        {brand.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="una-current-brand-info">
                            <span className="una-brand-badge">
                                {getBrandName(selectedBrandId)}
                            </span>
                        </div>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="una-tracking-settings-form">
                    {/* Colors Section */}
                    <div className="una-settings-section">
                        <h3>
                            <FaPalette />
                            {t('colorSettings', 'Color Settings')}
                        </h3>
                        
                        <div className="una-color-grid">
                            <div className="una-color-input-group">
                                <label htmlFor="primary_color">{t('primaryColor', 'Primary Color')}</label>
                                <input
                                    type="color"
                                    id="primary_color"
                                    value={formData.colors.primary_color}
                                    onChange={(e) => handleInputChange('colors', 'primary_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.primary_color}
                                    onChange={(e) => handleInputChange('colors', 'primary_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#2d2d2d"
                                />
                            </div>

                            <div className="una-color-input-group">
                                <label htmlFor="secondary_color">{t('secondaryColor', 'Secondary Color')}</label>
                                <input
                                    type="color"
                                    id="secondary_color"
                                    value={formData.colors.secondary_color}
                                    onChange={(e) => handleInputChange('colors', 'secondary_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.secondary_color}
                                    onChange={(e) => handleInputChange('colors', 'secondary_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#f8f9fa"
                                />
                            </div>

                            <div className="una-color-input-group">
                                <label htmlFor="background_color">{t('backgroundColor', 'Background Color')}</label>
                                <input
                                    type="color"
                                    id="background_color"
                                    value={formData.colors.background_color}
                                    onChange={(e) => handleInputChange('colors', 'background_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.background_color}
                                    onChange={(e) => handleInputChange('colors', 'background_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#ffffff"
                                />
                            </div>

                            <div className="una-color-input-group">
                                <label htmlFor="text_color">{t('textColor', 'Text Color')}</label>
                                <input
                                    type="color"
                                    id="text_color"
                                    value={formData.colors.text_color}
                                    onChange={(e) => handleInputChange('colors', 'text_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.text_color}
                                    onChange={(e) => handleInputChange('colors', 'text_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#333333"
                                />
                            </div>

                            <div className="una-color-input-group">
                                <label htmlFor="border_color">{t('borderColor', 'Border Color')}</label>
                                <input
                                    type="color"
                                    id="border_color"
                                    value={formData.colors.border_color}
                                    onChange={(e) => handleInputChange('colors', 'border_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.border_color}
                                    onChange={(e) => handleInputChange('colors', 'border_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#e0e0e0"
                                />
                            </div>

                            <div className="una-color-input-group">
                                <label htmlFor="success_color">{t('successColor', 'Success Color')}</label>
                                <input
                                    type="color"
                                    id="success_color"
                                    value={formData.colors.success_color}
                                    onChange={(e) => handleInputChange('colors', 'success_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.success_color}
                                    onChange={(e) => handleInputChange('colors', 'success_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#28a745"
                                />
                            </div>

                            <div className="una-color-input-group">
                                <label htmlFor="warning_color">{t('warningColor', 'Warning Color')}</label>
                                <input
                                    type="color"
                                    id="warning_color"
                                    value={formData.colors.warning_color}
                                    onChange={(e) => handleInputChange('colors', 'warning_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.warning_color}
                                    onChange={(e) => handleInputChange('colors', 'warning_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#ffc107"
                                />
                            </div>

                            <div className="una-color-input-group">
                                <label htmlFor="error_color">{t('errorColor', 'Error Color')}</label>
                                <input
                                    type="color"
                                    id="error_color"
                                    value={formData.colors.error_color}
                                    onChange={(e) => handleInputChange('colors', 'error_color', e.target.value)}
                                    className="una-color-input"
                                />
                                <input
                                    type="text"
                                    value={formData.colors.error_color}
                                    onChange={(e) => handleInputChange('colors', 'error_color', e.target.value)}
                                    className="una-color-text-input"
                                    placeholder="#dc3545"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Banner Section */}
                    <div className="una-settings-section">
                        <h3>
                            <FaImage />
                            {t('bannerSettings', 'Banner Settings')}
                        </h3>
                        
                        <div className="una-banner-settings">
                            <div className="una-form-group">
                                <label className="una-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.banner.enabled}
                                        onChange={(e) => handleInputChange('banner', 'enabled', e.target.checked)}
                                    />
                                    {t('enableBanner', 'Enable Banner')}
                                </label>
                            </div>

                            <div className="una-form-row">
                                <div className="una-form-group">
                                    <label htmlFor="banner_title">{t('bannerTitle', 'Banner Title')}</label>
                                    <input
                                        type="text"
                                        id="banner_title"
                                        value={formData.banner.title}
                                        onChange={(e) => handleInputChange('banner', 'title', e.target.value)}
                                        placeholder="Track Your Package"
                                    />
                                </div>
                                
                                <div className="una-form-group">
                                    <label htmlFor="banner_subtitle">{t('bannerSubtitle', 'Banner Subtitle')}</label>
                                    <input
                                        type="text"
                                        id="banner_subtitle"
                                        value={formData.banner.subtitle}
                                        onChange={(e) => handleInputChange('banner', 'subtitle', e.target.value)}
                                        placeholder="Optional subtitle"
                                    />
                                </div>
                            </div>

                            <div className="una-form-row">
                                <div className="una-form-group">
                                    <label htmlFor="banner_background_image">{t('bannerBackgroundImage', 'Background Image URL')}</label>
                                    <input
                                        type="url"
                                        id="banner_background_image"
                                        value={formData.banner.background_image}
                                        onChange={(e) => handleInputChange('banner', 'background_image', e.target.value)}
                                        placeholder="https://example.com/image.jpg"
                                    />
                                </div>
                                
                                <div className="una-form-group">
                                    <label htmlFor="banner_height">{t('bannerHeight', 'Banner Height (px)')}</label>
                                    <input
                                        type="number"
                                        id="banner_height"
                                        value={formData.banner.height}
                                        onChange={(e) => handleInputChange('banner', 'height', parseInt(e.target.value))}
                                        min="100"
                                        max="500"
                                        placeholder="200"
                                    />
                                </div>
                            </div>

                            <div className="una-form-row">
                                <div className="una-color-input-group">
                                    <label htmlFor="banner_background_color">{t('bannerBackgroundColor', 'Banner Background Color')}</label>
                                    <input
                                        type="color"
                                        id="banner_background_color"
                                        value={formData.banner.background_color}
                                        onChange={(e) => handleInputChange('banner', 'background_color', e.target.value)}
                                        className="una-color-input"
                                    />
                                    <input
                                        type="text"
                                        value={formData.banner.background_color}
                                        onChange={(e) => handleInputChange('banner', 'background_color', e.target.value)}
                                        className="una-color-text-input"
                                        placeholder="#f8f9fa"
                                    />
                                </div>

                                <div className="una-color-input-group">
                                    <label htmlFor="banner_text_color">{t('bannerTextColor', 'Banner Text Color')}</label>
                                    <input
                                        type="color"
                                        id="banner_text_color"
                                        value={formData.banner.text_color}
                                        onChange={(e) => handleInputChange('banner', 'text_color', e.target.value)}
                                        className="una-color-input"
                                    />
                                    <input
                                        type="text"
                                        value={formData.banner.text_color}
                                        onChange={(e) => handleInputChange('banner', 'text_color', e.target.value)}
                                        className="una-color-text-input"
                                        placeholder="#333333"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Branding Section */}
                    <div className="una-settings-section">
                        <h3>
                            <FaBuilding />
                            {t('brandingSettings', 'Branding Settings')}
                        </h3>
                        
                        <div className="una-branding-settings">
                            <div className="una-form-row">
                                <div className="una-form-group">
                                    <label htmlFor="company_name">{t('companyName', 'Company Name')}</label>
                                    <input
                                        type="text"
                                        id="company_name"
                                        value={formData.branding.company_name}
                                        onChange={(e) => handleInputChange('branding', 'company_name', e.target.value)}
                                        placeholder="Your Company Name"
                                    />
                                </div>
                                
                                <div className="una-form-group">
                                    <label htmlFor="company_logo">{t('companyLogo', 'Company Logo URL')}</label>
                                    <input
                                        type="url"
                                        id="company_logo"
                                        value={formData.branding.company_logo}
                                        onChange={(e) => handleInputChange('branding', 'company_logo', e.target.value)}
                                        placeholder="https://example.com/logo.png"
                                    />
                                </div>
                            </div>

                            <div className="una-form-group">
                                <label className="una-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={formData.branding.show_powered_by}
                                        onChange={(e) => handleInputChange('branding', 'show_powered_by', e.target.checked)}
                                    />
                                    {t('showPoweredBy', 'Show "Powered by" text')}
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Custom CSS Section */}
                    <div className="una-settings-section">
                        <h3>
                            <FaCode />
                            {t('customCss', 'Custom CSS')}
                        </h3>
                        
                        <div className="una-form-group">
                            <textarea
                                id="custom_css"
                                value={formData.custom_css}
                                onChange={(e) => handleCustomCssChange(e.target.value)}
                                rows="10"
                                placeholder="/* Add your custom CSS here */"
                                className="una-code-textarea"
                            />
                        </div>
                    </div>

                    {/* Form Actions */}
                    <div className="una-form-actions">
                        <button
                            type="button"
                            onClick={handleReset}
                            className="una-button una-button-secondary"
                        >
                            <FaUndo />
                            {t('reset', 'Reset')}
                        </button>
                        <button
                            type="submit"
                            className="una-button una-button-primary"
                            disabled={isSubmitting || !canManageSettings()}
                        >
                            {isSubmitting ? (
                                <>
                                    <FaSpinner className="una-spinner" />
                                    {t('saving', 'Saving...')}
                                </>
                            ) : (
                                <>
                                    <FaSave />
                                    {t('saveSettings', 'Save Settings')}
                                </>
                            )}
                        </button>
                    </div>
                </form>

                {/* Settings Info */}
                {settings && (
                    <div className="una-settings-info">
                        <p>
                            <strong>{t('brand', 'Brand')}:</strong> {getBrandName(selectedBrandId)}
                        </p>
                        <p>
                            {t('lastUpdated', 'Last updated')}: {formatDate(settings.updated_at)}
                        </p>
                        <p>
                            {t('created', 'Created')}: {formatDate(settings.created_at)}
                        </p>
                    </div>
                )}
            </div>

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="una-modal-overlay" onClick={() => setIsDeleteModalOpen(false)}>
                    <div className="una-modal-content una-delete-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="una-delete-warning">
                            <FaExclamationTriangle className="una-warning-icon" />
                            <h2>{t('confirmDeleteSettings', 'Confirm Delete Settings')}</h2>
                        </div>
                        
                        <p>
                            {t('deleteSettingsConfirmation', 'Are you sure you want to delete these tracking settings? This action cannot be undone.')}
                        </p>
                        
                        <p className="una-brand-info">
                            <strong>{t('brand', 'Brand')}:</strong> {getBrandName(selectedBrandId)}
                        </p>

                        <div className="una-form-actions">
                            <button
                                type="button"
                                onClick={() => setIsDeleteModalOpen(false)}
                                className="una-button una-button-secondary"
                            >
                                {t('cancel', 'Cancel')}
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="una-button una-button-danger"
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <FaSpinner className="una-spinner" />
                                        {t('deleting', 'Deleting...')}
                                    </>
                                ) : (
                                    <>
                                        <FaTrash />
                                        {t('deleteSettings', 'Delete Settings')}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Preview Modal */}
            {isPreviewModalOpen && (
                <div className="una-modal-overlay" onClick={() => setIsPreviewModalOpen(false)}>
                    <div className="una-modal-content una-preview-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="una-preview-header">
                            <h2>{t('previewTracking', 'Preview Tracking Page')}</h2>
                            <div className="una-preview-controls">
                                <select 
                                    value={previewStep} 
                                    onChange={(e) => setPreviewStep(e.target.value)}
                                    className="una-preview-step-selector"
                                >
                                    <option value="email-verification">Email Verification</option>
                                    <option value="code-verification">Code Verification</option>
                                    <option value="orders-list">Orders List</option>
                                    <option value="tracking-results">Tracking Results</option>
                                </select>
                                <button 
                                    className="una-close-button"
                                    onClick={() => setIsPreviewModalOpen(false)}
                                >
                                    <FaTimes />
                                </button>
                            </div>
                        </div>
                        
                        <div className="una-preview-content">
                            {renderPreviewContent()}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TrackingSettings;